import React, { useRef, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useLanguage } from '../../contexts/LanguageContext';
import DocumentDownloadService from '../../utils/documentDownloadService';

/**
 * Generic File Export Button Component
 * Base64, Blob ve URL formatlarında dönen dosyaları export eder
 * 
 * @param {Object} props
 * @param {Function} props.useExportQuery - RTK Query export hook'u (useLazyExportToExcelQuery gibi)
 * @param {Object} props.exportParams - Export için filtre parametreleri
 * @param {string} props.buttonLabel - Buton metni (opsiyonel)
 * @param {string} props.buttonClassName - Buton CSS class'ı (opsiyonel)
 * @param {string} props.icon - Buton ikonu (opsiyonel, default: pi-file-excel)
 * @param {boolean} props.disabled - Buton disabled durumu
 * @param {string} props.successMessage - Başarı mesajı (opsiyonel)
 * @param {string} props.errorMessage - Hata mesajı (opsiyonel)
 * @param {string} props.tooltip - Tooltip metni (opsiyonel)
 * @param {Function} props.onExportStart - Export başlangıcında callback
 * @param {Function} props.onExportSuccess - Export başarılı olunca callback
 * @param {Function} props.onExportError - Export hata olunca callback
 * @param {Function} props.fileNameExtractor - Dosya adı çıkarma fonksiyonu (opsiyonel)
 * @param {Function} props.dataExtractor - Veri çıkarma fonksiyonu (opsiyonel)
 * @param {Function} props.contentTypeExtractor - Content-Type çıkarma fonksiyonu (opsiyonel)
 * @param {string} props.exportFormat - Export formatı: 'auto' | 'base64' | 'blob' | 'url' (default: 'auto')
 * @param {string} props.defaultFileName - Default dosya adı (opsiyonel, default: 'export')
 * @param {string} props.defaultContentType - Default content type (opsiyonel)
 */
const GenericFileExportButton = ({
    useExportQuery,
    exportParams = {},
    buttonLabel,
    buttonClassName = "p-button-success",
    icon = "pi pi-file-excel",
    disabled = false,
    successMessage,
    errorMessage,
    tooltip,
    onExportStart,
    onExportSuccess,
    onExportError,
    fileNameExtractor,
    dataExtractor,
    contentTypeExtractor,
    exportFormat = 'auto',
    defaultFileName = 'export',
    defaultContentType = 'application/octet-stream'
}) => {
    const { t } = useLanguage();
    const toast = useRef(null);

    const [triggerExport, { isLoading }] = useExportQuery();

    const downloadService = useMemo(() => new DocumentDownloadService(toast), [toast]);

    // Dosya uzantısını content-type'dan çıkar
    const getFileExtensionFromContentType = (contentType) => {
        const mimeMap = {
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
            'application/vnd.ms-excel': 'xls',
            'application/pdf': 'pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
            'application/msword': 'doc',
            'text/csv': 'csv',
            'application/zip': 'zip',
            'image/png': 'png',
            'image/jpeg': 'jpg',
            'image/jpg': 'jpg',
            'text/plain': 'txt',
            'application/json': 'json'
        };
        return mimeMap[contentType] || '';
    };

    // Dosya adını normalize et
    const normalizeFileName = (fileName, contentType) => {
        if (!fileName || fileName.trim() === '') {
            const extension = getFileExtensionFromContentType(contentType);
            return extension ? `${defaultFileName}.${extension}` : defaultFileName;
        }

        // Eğer dosya adında uzantı yoksa content-type'dan ekle
        if (!fileName.includes('.')) {
            const extension = getFileExtensionFromContentType(contentType);
            return extension ? `${fileName}.${extension}` : fileName;
        }

        return fileName;
    };

    // Default extractors
    const defaultFileNameExtractor = (result) => {
        return result?.fileName || '';
    };

    const defaultDataExtractor = (result) => {
        // Base64 data
        if (result?.fileBase64 || result?.base64 || result?.data) {
            return result.fileBase64 || result.base64 || result.data;
        }
        // Blob data
        if (result?.blob) {
            return result.blob;
        }
        // URL data
        if (result?.url || result?.downloadUrl) {
            return result.url || result.downloadUrl;
        }
        return null;
    };

    const defaultContentTypeExtractor = (result) => {
        return result?.contentType || result?.mimeType || result?.type || defaultContentType;
    };

    // Başarı mesajı oluştur
    const getDefaultSuccessMessage = (result, fileName) => {
        const recordCount = result?.recordCount || result?.count || 0;

        if (recordCount > 0) {
            return t('excelExport.messages.exportSuccessWithCount', {
                count: recordCount,
                fileName
            }) || `${recordCount} kayıt başarıyla ${fileName} dosyasına aktarıldı.`;
        }
        return t('excelExport.messages.exportSuccess', { fileName }) || `Dosya başarıyla indirildi: ${fileName}`;
    };

    // Base64'ü Blob'a çevir
    const base64ToBlob = (base64Data, contentType) => {
        try {
            const base64String = base64Data.includes(',')
                ? base64Data.split(',')[1]
                : base64Data;

            const byteCharacters = atob(base64String);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            return new Blob([byteArray], { type: contentType });
        } catch (error) {
            console.error('Base64 to Blob conversion error:', error);
            throw new Error(t('excelExport.messages.conversionError') || 'Dosya dönüştürme hatası');
        }
    };

    // Dosyayı indir
    const downloadFile = (data, fileName, contentType) => {
        try {
            let blob;

            // Format belirleme
            const format = exportFormat === 'auto'
                ? detectFormat(data)
                : exportFormat;

            switch (format) {
                case 'blob':
                    // Zaten Blob formatında
                    blob = data;
                    break;

                case 'base64':
                    // Base64'ü Blob'a çevir
                    blob = base64ToBlob(data, contentType);
                    break;

                case 'url':
                    // Data URL formatı - doğrudan link ile indir
                    if (typeof data === 'string' && data.startsWith('data:')) {
                        const link = document.createElement('a');
                        link.href = data;
                        link.download = fileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        return;
                    }
                    // HTTP URL - yeni sekmede aç veya indir
                    else if (typeof data === 'string' && (data.startsWith('http://') || data.startsWith('https://'))) {
                        const link = document.createElement('a');
                        link.href = data;
                        link.download = fileName;
                        link.target = '_blank';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        return;
                    }
                    throw new Error('Geçersiz URL formatı');

                default:
                    throw new Error(t('excelExport.messages.unsupportedFormat') || 'Desteklenmeyen format');
            }

            // Blob ile indirme
            if (blob instanceof Blob) {
                downloadService.downloadBlob(blob, fileName, false);
            } else {
                throw new Error('Dosya oluşturulamadı');
            }

        } catch (error) {
            console.error('Download error:', error);
            throw error;
        }
    };

    // Veri formatını otomatik algıla
    const detectFormat = (data) => {
        if (data instanceof Blob) {
            return 'blob';
        }
        if (typeof data === 'string') {
            if (data.startsWith('data:')) {
                return 'url';
            }
            if (data.startsWith('http://') || data.startsWith('https://')) {
                return 'url';
            }
            return 'base64';
        }
        return 'unknown';
    };

    const handleExport = async () => {
        try {
            if (onExportStart) {
                onExportStart(exportParams);
            }

            const result = await triggerExport(exportParams).unwrap();

            // Extractors ile veriyi çıkar
            const exportData = (dataExtractor || defaultDataExtractor)(result);
            const rawFileName = (fileNameExtractor || defaultFileNameExtractor)(result);
            const contentType = (contentTypeExtractor || defaultContentTypeExtractor)(result);

            if (!exportData) {
                throw new Error(t('excelExport.messages.noData') || 'İndirilecek veri bulunamadı');
            }

            // Dosya adını normalize et
            const fileName = normalizeFileName(rawFileName, contentType);

            // Dosyayı indir
            downloadFile(exportData, fileName, contentType);

            // Başarı mesajı
            const finalSuccessMessage = successMessage || getDefaultSuccessMessage(result, fileName);
            downloadService.showToast('success', t('common.success') || 'Başarılı', finalSuccessMessage, 5000);

            if (onExportSuccess) {
                onExportSuccess(result, fileName);
            }

        } catch (error) {
            console.error('Export error:', error);

            const finalErrorMessage = errorMessage ||
                error?.data?.errorMessage ||
                error?.data?.message ||
                error?.message ||
                t('excelExport.messages.exportError') ||
                'Export işlemi sırasında bir hata oluştu';

            downloadService.showToast('error', t('common.error') || 'Hata', finalErrorMessage, 5000);

            if (onExportError) {
                onExportError(error, exportParams);
            }
        }
    };

    const getButtonLabel = () => {
        if (buttonLabel) return buttonLabel;
        return t('excelExport.exportButton') || 'Dışa Aktar';
    };

    const getTooltip = () => {
        if (tooltip) return tooltip;
        return t('excelExport.exportTooltip') || 'Dosyayı dışa aktar';
    };

    return (
        <>
            <Toast ref={toast} />
            <Button
                icon={icon}
                label={getButtonLabel()}
                className={buttonClassName}
                loading={isLoading}
                disabled={disabled || isLoading}
                onClick={handleExport}
                tooltip={getTooltip()}
                tooltipOptions={{ position: 'top' }}
            />
        </>
    );
};

export default GenericFileExportButton;